// src/components/AccessibleModal.jsx
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "../styles/loopLayers.css";

export default function AccessibleModal({
  open,
  onClose,
  labelledBy,
  children,
  width = 560,
  dialogClassName = "",
  dialogStyle = {},
  autoFocus = false,
  layer = "auto",
}) {
  const dialogRef = useRef(null);
  const lastFocusedRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;

    lastFocusedRef.current = document.activeElement;

    // LOOP R0.2.2 — Focus stability fix:
    // Do not automatically focus the first input by default.
    // In large controlled modals, parent re-renders can recreate `onClose` and
    // retrigger a focus effect. Keeping focus where the user clicks prevents
    // the cursor from jumping out while typing.
    if (autoFocus) {
      window.setTimeout(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;
        const focusable = dialog.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable && focusable.length > 0) focusable[0].focus();
        else dialog.focus();
      }, 0);
    }

    function onKey(e) {
      if (e.key === "Escape") {
        e.stopPropagation();
        if (onCloseRef.current) onCloseRef.current();
        return;
      }

      if (e.key !== "Tab") return;

      const dialog = dialogRef.current;
      if (!dialog) return;

      const focusable = Array.from(
        dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter((el) => !el.disabled && el.getAttribute("aria-hidden") !== "true");

      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }

      const idx = focusable.indexOf(document.activeElement);
      if (e.shiftKey) {
        if (idx <= 0) {
          focusable[focusable.length - 1].focus();
          e.preventDefault();
        }
      } else if (idx === focusable.length - 1) {
        focusable[0].focus();
        e.preventDefault();
      }
    }

    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      // Do not force-restore focus while inputs are being edited. Focus restore
      // only matters when the modal truly closes; using open-only dependency
      // prevents repeated blur/focus cycles during parent renders.
      try {
        const last = lastFocusedRef.current;
        if (last && last.focus && document.contains(last)) last.focus();
      } catch (_) {}
    };
  }, [open, autoFocus]);

  if (!open) return null;

  const classNameText = String(dialogClassName || "");
  const isNestedModal =
    layer === "nested" ||
    classNameText.includes("loop-nested-settings-modal") ||
    classNameText.includes("loop-payment") ||
    classNameText.includes("loop-availability") ||
    classNameText.includes("loop-new-customer") ||
    classNameText.includes("loop-settings");
  const isStackedModal = layer === "stacked";
  const modalLayerZIndex = layer === "activation" ? "var(--loop-z-activation-modal-backdrop, 5600)" : isStackedModal
    ? "var(--loop-z-stacked-modal-backdrop, 5500)"
    : isNestedModal
    ? "var(--loop-z-nested-modal-backdrop, 5000)"
    : "var(--loop-z-modal-backdrop, 4000)";
  const dialogZIndex = 1;

  const baseStyle = {
    width,
    background: "#fff",
    borderRadius: 8,
    padding: 16,
    boxShadow: "0 8px 32px rgba(2,6,23,0.16)",
    position: "relative",
    zIndex: dialogZIndex,
    ...dialogStyle,
  };

  const modalNode = (
    <div
      role="presentation"
      className={`loop-accessible-modal-layer${isNestedModal ? " is-nested-modal" : ""}${isStackedModal ? " is-stacked-modal" : ""}${layer === "activation" ? " is-activation-modal" : ""}`}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: modalLayerZIndex,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        isolation: "isolate",
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && onCloseRef.current) onCloseRef.current();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={dialogClassName || undefined}
        onMouseDown={(e) => e.stopPropagation()}
        tabIndex={-1}
        style={baseStyle}
      >
        {children}
      </div>
    </div>
  );

  if (typeof document === "undefined" || !document.body) return modalNode;
  return createPortal(modalNode, document.body);
}
